import * as React from "react";
import { CreditCard, Truck, RefreshCcw, Mail, MessageCircleQuestionMark } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const categories = [{
  id: "logistics",
  title: "Logistics & Planning",
  icon: Truck
}, {
  id: "pricing",
  title: "Pricing & Payments",
  icon: CreditCard
}, {
  id: "safety",
  title: "Safety & Insurance",
  icon: RefreshCcw
}];
const faqs = [{
  id: "p1",
  categoryId: "pricing",
  question: "How do you calculate the cost of a move?",
  answer: "Our pricing is based on the volume of items, the distance between locations, and any additional services like professional packing or furniture disassembly. We provide transparent, fixed-rate quotes after a virtual or in-person assessment."
}, {
  id: "p2",
  categoryId: "pricing",
  question: "Are there any hidden fees on moving day?",
  answer: "No. At SHIFT, we believe in Kinetic Precision. Your final quote includes fuel, labor, and equipment. Unless the scope of work changes significantly, the price you see is the price you pay."
}, {
  id: "p3",
  categoryId: "pricing",
  question: "What payment methods do you accept?",
  answer: "We accept all major credit cards, bank transfers, and digital payments. A small deposit is required to secure your moving date, with the balance due upon successful delivery."
}, {
  id: "s1",
  categoryId: "logistics",
  question: "How far in advance should I book my move?",
  answer: "To ensure your preferred date, we recommend booking at least 2-4 weeks in advance. However, we specialize in efficient logistics and can often accommodate last-minute 'shifts' depending on truck availability."
}, {
  id: "s2",
  categoryId: "logistics",
  question: "Do you provide packing materials?",
  answer: "Yes, we offer full-service packing including heavy-duty boxes, bubble wrap, and custom crating for fragile items. You can also purchase materials from us if you prefer to pack yourself."
}, {
  id: "r1",
  categoryId: "safety",
  question: "Is my furniture insured during the move?",
  answer: "Absolutely. Every SHIFT move includes standard liability coverage. For high-value items or complete peace of mind, we offer comprehensive valuation protection plans that cover the full replacement cost."
}, {
  id: "r2",
  categoryId: "safety",
  question: "How do you handle fragile or antique items?",
  answer: "Our team is trained in specialized handling techniques. We use custom padding, floor runners, and secure strapping systems in our fully equipped trucks to ensure every item arrives in its original condition."
}];
export default function CommonQuestions() {
  return <section data-section-id="224891" id="common-questions" className="w-full py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        
        {}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-md mb-4">
            <MessageCircleQuestionMark className="size-6 text-primary" />
          </div>
          <h2 className="font-default text-3xl md:text-5xl font-bold tracking-tight mb-4 uppercase">
            Common Questions
          </h2>
          <p className="font-default text-muted-foreground text-lg max-w-2xl mx-auto">
            Everything you need to know about planning your next move with SHIFT. Reliable, stress-free, and professional.
          </p>
        </div>

        {}
        <div className="space-y-12">
          {categories.map((category, i) => {
          const categoryFaqs = faqs.filter(faq => faq.categoryId === category.id);
          const Icon = category.icon;
          return <div key={category.id} data-index={i} className="bg-card rounded-md p-6 md:p-8 shadow-md border border-border">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                  <div className="p-2 bg-secondary/5 rounded-md">
                    <Icon className="size-5 text-primary" />
                  </div>
                  <h3 className="font-default text-xl font-bold text-card-foreground uppercase tracking-tight">
                    {category.title}
                  </h3>
                </div>

                <Accordion type="single" collapsible className="w-full">
                  {categoryFaqs.map((faq, j) => <AccordionItem key={faq.id} value={faq.id} data-index={j} className="border-border last:border-0">
                      <AccordionTrigger className="text-left font-default font-semibold text-lg hover:text-primary transition-colors py-4 data-[state=open]:text-primary">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="font-default text-muted-foreground leading-relaxed pb-5 text-base">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 text-center bg-secondary rounded-md p-8 border border-primary/20 shadow-lg">
          <h4 className="font-default text-2xl font-bold mb-2 text-secondary-foreground uppercase">
            Still have questions?
          </h4>
          <p className="font-default text-secondary-foreground/80 mb-6 max-w-md mx-auto">
            Our logistics experts are standing by to help you plan your perfect relocation.
          </p>
          <Button asChild size="lg" className="rounded-md px-8 bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300">
            <Link to="/GetQuotePage">
              <Mail className="mr-2 size-4" />
              Contact Support
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}